import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, ChevronRightIcon, SmartphoneIcon, GlobeIcon, PhoneIcon, MailIcon, MessageSquareIcon, SparklesIcon, FileTextIcon, WrenchIcon, BoxIcon, DatabaseIcon, BriefcaseIcon, PlugIcon, SendIcon, WandSparklesIcon, UserCogIcon, RefreshCcwIcon, ClipboardCheckIcon, ListFilterIcon, CloudDownloadIcon, BrainIcon, FilterIcon, LayersIcon, SettingsIcon, BarChart3Icon, UsersIcon, ClockIcon, MapPinIcon, CheckCircle2Icon, CheckIcon, CalendarIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
type Bilingual = {
  en: string;
  fr: string;
};
type Item = {
  icon: typeof SmartphoneIcon;
  title: Bilingual;
  desc: Bilingual;
};
const CHANNELS: Item[] = [{
  icon: SmartphoneIcon,
  title: {
    fr: 'Application mobile',
    en: 'Mobile app'
  },
  desc: {
    fr: 'Demandes et formulaires',
    en: 'Requests and forms'
  }
}, {
  icon: GlobeIcon,
  title: {
    fr: 'Site web',
    en: 'Website'
  },
  desc: {
    fr: 'Formulaire citoyen',
    en: 'Resident form'
  }
}, {
  icon: PhoneIcon,
  title: {
    fr: 'Téléphone',
    en: 'Phone'
  },
  desc: {
    fr: 'Centre d’appels',
    en: 'Call centre'
  }
}, {
  icon: MailIcon,
  title: {
    fr: 'Courriel',
    en: 'Email'
  },
  desc: {
    fr: 'Demandes entrantes',
    en: 'Incoming requests'
  }
}, {
  icon: MessageSquareIcon,
  title: {
    fr: 'Réseaux sociaux',
    en: 'Social media'
  },
  desc: {
    fr: 'Messages et commentaires',
    en: 'Messages and comments'
  }
}, {
  icon: SparklesIcon,
  title: {
    fr: 'ATNA',
    en: 'ATNA'
  },
  desc: {
    fr: 'Comprend et oriente',
    en: 'Understands and routes'
  }
}];
const CONNECT: Item[] = [{
  icon: DatabaseIcon,
  title: {
    fr: 'CRM / ERP',
    en: 'CRM / ERP'
  },
  desc: {
    fr: 'Dossiers et données',
    en: 'Records and data'
  }
}, {
  icon: BriefcaseIcon,
  title: {
    fr: 'Systèmes métiers',
    en: 'Business systems'
  },
  desc: {
    fr: 'Permis · inspections · opérations',
    en: 'Permits · inspections · operations'
  }
}, {
  icon: PlugIcon,
  title: {
    fr: 'API et partenaires',
    en: 'APIs & partners'
  },
  desc: {
    fr: 'Services spécialisés',
    en: 'Specialized services'
  }
}, {
  icon: SendIcon,
  title: {
    fr: 'Communications',
    en: 'Communications'
  },
  desc: {
    fr: 'Courriel · SMS · notifications',
    en: 'Email · SMS · notifications'
  }
}];
const REPLACE: Item[] = [{
  icon: WandSparklesIcon,
  title: {
    fr: 'Création et qualification',
    en: 'Creation & qualification'
  },
  desc: {
    fr: 'Compréhension par l’IA',
    en: 'AI understanding'
  }
}, {
  icon: SendIcon,
  title: {
    fr: 'Communications',
    en: 'Communications'
  },
  desc: {
    fr: 'Citoyens et équipes',
    en: 'Residents and teams'
  }
}, {
  icon: UserCogIcon,
  title: {
    fr: 'Attribution et traitement',
    en: 'Assignment & handling'
  },
  desc: {
    fr: 'Équipes et intervenants',
    en: 'Teams and responders'
  }
}, {
  icon: ClipboardCheckIcon,
  title: {
    fr: 'Résolution et fermeture',
    en: 'Resolution & closure'
  },
  desc: {
    fr: 'Suivi et validation',
    en: 'Tracking and validation'
  }
}, {
  icon: RefreshCcwIcon,
  title: {
    fr: 'SLA et remontées hiérarchiques',
    en: 'SLAs & escalations'
  },
  desc: {
    fr: 'Règles et automatisation',
    en: 'Rules and automation'
  }
}, {
  icon: ListFilterIcon,
  title: {
    fr: 'Historique et analytique',
    en: 'History & analytics'
  },
  desc: {
    fr: 'Tableaux de bord',
    en: 'Dashboards'
  }
}];
const STAGES: Item[] = [{
  icon: CloudDownloadIcon,
  title: {
    fr: 'Capte',
    en: 'Capture'
  },
  desc: {
    fr: 'Reçoit la demande',
    en: 'Receives the request'
  }
}, {
  icon: BrainIcon,
  title: {
    fr: 'Comprend',
    en: 'Understand'
  },
  desc: {
    fr: 'Analyse et classe',
    en: 'Analyzes and classifies'
  }
}, {
  icon: FilterIcon,
  title: {
    fr: 'Priorise',
    en: 'Prioritize'
  },
  desc: {
    fr: 'Évalue l’urgence et l’impact',
    en: 'Evaluates urgency and impact'
  }
}, {
  icon: LayersIcon,
  title: {
    fr: 'Orchestre',
    en: 'Orchestrate'
  },
  desc: {
    fr: 'Achemine vers la bonne équipe, dans ATNA ou dans vos systèmes',
    en: 'Routes to the right team, in ATNA or your systems'
  }
}, {
  icon: SettingsIcon,
  title: {
    fr: 'Agit',
    en: 'Act'
  },
  desc: {
    fr: 'Crée les tâches, les interventions et les suivis',
    en: 'Creates tasks, interventions and follow-ups'
  }
}, {
  icon: SendIcon,
  title: {
    fr: 'Informe',
    en: 'Inform'
  },
  desc: {
    fr: 'Tient le citoyen informé',
    en: 'Keeps the resident updated'
  }
}, {
  icon: BarChart3Icon,
  title: {
    fr: 'Mesure',
    en: 'Measure'
  },
  desc: {
    fr: 'Transforme l’activité en données utiles à la décision',
    en: 'Turns activity into intelligence'
  }
}];
const BENEFITS: {
  icon: typeof UsersIcon;
  fr: [string, string];
  en: [string, string];
}[] = [{
  icon: UsersIcon,
  fr: ['Plus de capacité', 'pour vos équipes'],
  en: ['More capacity', 'for your teams']
}, {
  icon: ClockIcon,
  fr: ['Des délais plus courts', 'pour les citoyens'],
  en: ['Shorter wait times', 'for residents']
}, {
  icon: BarChart3Icon,
  fr: ['Une visibilité complète', 'pour mieux décider'],
  en: ['Complete visibility', 'for better decisions']
}];
const CHANNEL_X = [8.33, 25, 41.67, 58.33, 75, 91.67];
const card = 'rounded-2xl border border-[#EAEAF2] bg-white shadow-[0_8px_24px_-18px_rgba(40,36,120,0.25)]';
function IconDot({
  icon: Icon,
  size = 'md'
}: {
  icon: typeof SmartphoneIcon;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim = size === 'lg' ? 'w-11 h-11' : size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const ic = size === 'lg' ? 'w-5 h-5' : size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  return <span className={`${dim} shrink-0 rounded-full bg-[#F1F0FD] flex items-center justify-center`} aria-hidden="true" data-id="element-6140" data-mp="22dajjfuvgk9v">
      <Icon className={`${ic} text-[#4F46E5]`} strokeWidth={1.7} data-id="element-6141" data-mp="1tiw8jphdjvwk" />
    </span>;
}
function OptionHeader({
  icon,
  title,
  sub
}: {
  icon: typeof SmartphoneIcon;
  title: string;
  sub: string;
}) {
  return <div className="flex items-center gap-4" data-id="element-6142" data-mp="2lz6zzuh93c4r">
      <IconDot icon={icon} size="lg" data-id="element-6143" data-mp="2m103x20hbi92" />
      <div data-id="element-6144" data-mp="2h8qkm4tvdgfr">
        <p className="font-sans text-[14px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5]" data-id="element-6145" data-mp="2h870qiknshz3">{title}</p>
        <p className="font-sans text-[12.5px] text-[#2A2F3C] mt-0.5" data-id="element-6146" data-mp="304pdx1cwuz8y">{sub}</p>
      </div>
    </div>;
}
function OrBoth({
  t
}: {
  t: (fr: string, en: string) => string;
}) {
  return <div className="w-[76px] h-[76px] rounded-full border border-[#E2E0F6] bg-white shadow-[0_10px_24px_-14px_rgba(79,70,229,0.4)] flex flex-col items-center justify-center text-center" data-id="element-6147" data-mp="2rd6y4rjvmlge">
      <span className="font-sans text-[17px] font-semibold text-[#4F46E5] leading-none" data-id="element-6148" data-mp="2hehht37mp929">{t('OU', 'OR')}</span>
      <span className="font-sans text-[9.5px] font-semibold tracking-[0.06em] uppercase text-[#4F46E5] mt-1" data-id="element-6149" data-mp="2me3rj2o9076b">{t('les deux', 'both')}</span>
    </div>;
}
function StepArrow() {
  return <div className="flex items-center justify-center py-2 lg:py-0" aria-hidden="true" data-id="element-6150" data-mp="2cf4smbtabpwi">
      <ArrowRightIcon className="hidden lg:block w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-6151" data-mp="2ch7q1gwrolb9" />
      <ArrowDownIcon className="lg:hidden w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-6152" data-mp="6klmitj2ah0u" />
    </div>;
}
export function RequestOrchestration() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  const t = (fr: string, en: string) => isFR ? fr : en;
  return <div className="relative max-w-[1440px] mx-auto" data-id="element-6153" data-mp="24wf2q3q2unnl">
      <div className="pointer-events-none absolute right-[5%] top-[10%] w-[55%] h-[70%] rounded-full bg-[#EEF0FF] opacity-60 blur-[120px]" aria-hidden="true" data-id="element-6154" data-mp="24x6pcp1p5b1q" />

      <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,35fr)_minmax(0,65fr)] gap-14 lg:gap-10 items-start" data-id="element-6155" data-mp="2esi1r0pmu66b">
        {/* LEFT — message */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="lg:pt-10" data-id="element-6156" data-mp="2nuho88jf22gn">
          <p className="font-sans text-[13px] font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-6157" data-mp="tgyr2ztaslvv">
            {t('Requêtes ATNA', 'ATNA Requests')}
          </p>
          <h2 className="font-serif text-[3rem] sm:text-[3.8rem] xl:text-[4.5rem] text-[#121212] leading-[1.02] tracking-tight mt-6" data-id="element-6158" data-mp="3kwimj2fw5l3">
            {t('Connectez', 'Connect')}
            <span className="block" data-id="element-6159" data-mp="cdrcyhqdcgw3">{t('ce qui existe.', 'what exists.')}</span>
            <span className="block text-[#4F46E5]" data-id="element-6160" data-mp="hdcnlpewidon">{t('Remplacez', 'Replace')}</span>
            <span className="block" data-id="element-6161" data-mp="hfgt2bsg7kr1">{t('ce qui n’a plus', 'what no longer')}</span>
            <span className="block" data-id="element-6162" data-mp="3guandy8kawo5">{t('besoin d’exister.', 'needs to exist.')}</span>
          </h2>
          <p className="font-sans text-[17px] xl:text-[18.5px] text-[#5B6478] leading-[1.65] mt-8 max-w-[500px]" data-id="element-6163" data-mp="3kucree1rs26l">
            {t('Requêtes ATNA peut orchestrer vos systèmes existants ou prendre en charge l’ensemble du processus — de la demande à la résolution.', 'ATNA Requests can orchestrate your existing systems or take over the entire process — from request to resolution.')}
          </p>
          <p className="font-sans text-[15px] text-[#5B6478] mt-6" data-id="element-6164" data-mp="2ujst2973m0sa">
            {t('Application · Web · Téléphone · Courriel · Réseaux sociaux · ATNA', 'App · Web · Phone · Email · Social media · ATNA')}
          </p>

          <div className="flex flex-wrap items-center gap-x-9 gap-y-4 mt-9" data-id="element-6165" data-mp="2ksg1zjt4jenb">
            <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
            location: 'Requests Connect or Replace'
          })} className="inline-flex items-center gap-3 rounded-md bg-[#4F46E5] px-7 py-4 font-sans text-[16px] text-white transition-colors duration-150 hover:bg-[#4338CA]" data-id="element-6166" data-mp="2fuejazzm1hcd">
              {t('Demander une démo', 'Request a demo')}
              <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-6167" data-mp="1wxlnavz262ec" />
            </Link>
            <Link to="/brossard" className="inline-flex items-center gap-2 font-sans text-[16px] text-[#121212] transition-colors duration-150 hover:text-[#4F46E5]" data-id="element-6168" data-mp="3i69i4tzy40">
              {t('Découvrir Brossard', 'Explore Brossard')}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-6169" data-mp="16gfestbasa7" />
            </Link>
          </div>

          <ul className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0" data-id="element-6170" data-mp="g37ibc272m4k">
            {BENEFITS.map(({
            icon: Icon,
            fr,
            en
          }, i) => {
            const [a, b] = isFR ? fr : en;
            return <li key={en[0]} className={`flex sm:flex-col items-center sm:items-start gap-4 sm:px-5 ${i === 0 ? 'sm:pl-0' : 'sm:border-l sm:border-[#E4E6EC]'}`} data-id="element-6171" data-mp="3jkplh670v4yn" data-mpi={i}>
                  <span className="w-14 h-14 shrink-0 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-6172" data-mp="3oir3rowmj092">
                    <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-6173" data-mp="30sn1plz338k6" />
                  </span>
                  <span className="font-sans text-[14px] text-[#121212] leading-snug" data-id="element-6174" data-mp="2vtahza3iehlt">
                    {a}
                    <span className="block" data-id="element-6175" data-mp="1bagho306u775">{b}</span>
                  </span>
                </li>;
          })}
          </ul>
        </motion.div>

        {/* RIGHT — product story */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.06
      }} data-id="element-6176" data-mp="11fg591qxa4yj">
          {/* Channels */}
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-id="element-6177" data-mp="1z25bjnsnr3q7">
            {CHANNELS.map(({
            icon,
            title,
            desc
          }) => <li key={title.en} className={`${card} px-2 py-4 text-center flex flex-col items-center`} data-id="element-6178" data-mp="1l1b71g8qdmwt">
                <IconDot icon={icon} data-id="element-6179" data-mp="1p1mokk5sr742" />
                <span className="font-sans text-[12.5px] font-medium text-[#121212] mt-2.5 leading-tight" data-id="element-6180" data-mp="9mxisaa2f5g9">{pick(title)}</span>
                <span className="font-sans text-[10.5px] text-[#8A90A0] mt-1 leading-snug" data-id="element-6181" data-mp="dpc18t9to370">{pick(desc)}</span>
              </li>)}
          </ul>

          {/* Converging connectors */}
          <div className="hidden lg:block relative h-12" aria-hidden="true" data-id="element-6182" data-mp="2bcwdpht19wp5">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" data-id="element-6183" data-mp="26kmrocv1hgs5">
              {CHANNEL_X.map(x => <path key={x} d={`M${x} 0 C ${x} 55, 50 45, 50 100`} stroke="#8B85EA" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6184" data-mp="21jz11saq31yg" />)}
            </svg>
          </div>
          <div className="lg:hidden flex justify-center py-3" aria-hidden="true" data-id="element-6185" data-mp="1nmiumgsff86p">
            <ArrowDownIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-6186" data-mp="1ioqs6su5o3o9" />
          </div>

          {/* Central platform */}
          <div className="mx-auto max-w-[440px] rounded-2xl border border-[#E2E0F6] bg-[#F8F7FE] px-6 py-5 flex items-center gap-5 shadow-[0_14px_36px_-22px_rgba(79,70,229,0.45)]" data-id="element-6187" data-mp="3hziewn7r4qkb">
            <span className="w-14 h-14 shrink-0 rounded-full bg-[#4F46E5] flex items-center justify-center" aria-hidden="true" data-id="element-6188" data-mp="3lhpp1zmccd7e">
              <FileTextIcon className="w-6 h-6 text-white" strokeWidth={1.7} data-id="element-6189" data-mp="3phgarox0g352" />
            </span>
            <div data-id="element-6190" data-mp="3ucm438sqsk36">
              <p className="font-serif text-[1.6rem] md:text-[1.8rem] text-[#121212] leading-tight" data-id="element-6191" data-mp="3489iindf82d">
                {t('Requêtes ATNA', 'ATNA Requests')}
              </p>
              <p className="font-sans text-[13px] text-[#5B6478] mt-1" data-id="element-6192" data-mp="32lg36q576icu">
                {t('Une plateforme. Deux façons d’avancer.', 'One platform. Two ways forward.')}
              </p>
            </div>
          </div>

          {/* Branching connectors */}
          <div className="hidden lg:block relative h-9" aria-hidden="true" data-id="element-6193" data-mp="3gk5yq1cdc9z3">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" data-id="element-6194" data-mp="quc3dyarbtg6">
              <path d="M34 20 C 25 20, 25 20, 25 100" stroke="#8B85EA" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6195" data-mp="vrugm0vt4utz" />
              <path d="M66 20 C 75 20, 75 20, 75 100" stroke="#8B85EA" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6196" data-mp="24qdomw3lstbp" />
              <path d="M50 0 L50 100" stroke="#8B85EA" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-6197" data-mp="1zt3syypmflff" />
            </svg>
          </div>
          <div className="lg:hidden flex justify-center py-3" aria-hidden="true" data-id="element-6198" data-mp="28r60zy7rvec7">
            <ArrowDownIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-6199" data-mp="2nogbb48oie6c" />
          </div>

          {/* Connect | OR BOTH | Replace */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_92px_minmax(0,1.15fr)] items-stretch gap-4 lg:gap-0" data-id="element-6200" data-mp="2iok6x3w2kkq9">
            <div className={`${card} p-5`} data-id="element-6201" data-mp="2wg8gp0iuix42">
              <OptionHeader icon={WrenchIcon} title={t('Connecter', 'Connect')} sub={t('Orchestrez vos systèmes existants.', 'Orchestrate your existing systems.')} data-id="element-6202" data-mp="2sfomo5tkk9m5" />
              <ul className="mt-4 space-y-2" data-id="element-6203" data-mp="31h5w55ch1d1v">
                {CONNECT.map(({
                icon: Icon,
                title,
                desc
              }) => <li key={title.en} className="flex items-center gap-3.5 rounded-xl border border-[#EFEFF5] bg-[#FCFCFE] px-3.5 py-2.5" data-id="element-6204" data-mp="3l37pibw0hxkc">
                    <Icon className="w-[18px] h-[18px] text-[#5B6478] shrink-0" strokeWidth={1.6} aria-hidden="true" data-id="element-6205" data-mp="3pyn1szedflen" />
                    <span className="min-w-0" data-id="element-6206" data-mp="3g2juzg8kqyld">
                      <span className="block font-sans text-[12.5px] text-[#121212] leading-tight" data-id="element-6207" data-mp="3g18r7gf4pqu9">{pick(title)}</span>
                      <span className="block font-sans text-[11px] text-[#8A90A0] mt-0.5" data-id="element-6208" data-mp="329c2lmco5z2m">{pick(desc)}</span>
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="relative flex items-center justify-center" data-id="element-6209" data-mp="2xcbt11gm2e6r">
              <span className="hidden lg:block absolute left-0 right-0 top-1/2 h-px bg-[#8B85EA]" aria-hidden="true" data-id="element-6210" data-mp="2xclj0p1nolcg" />
              <span className="hidden lg:block absolute left-1/2 top-[-4px] bottom-1/2 w-px bg-[#8B85EA]" aria-hidden="true" data-id="element-6211" data-mp="2jex0voyz3sb3" />
              <span className="relative flex flex-col lg:flex-row items-center gap-2" data-id="element-6212" data-mp="tvxeric2cpm9">
                <span className="lg:hidden font-sans text-[#4F46E5]" aria-hidden="true" data-id="element-6213" data-mp="p4yvc9vs3bes">↕</span>
                <OrBoth t={t} data-id="element-6214" data-mp="k5m0p2yo101n" />
                <span className="lg:hidden font-sans text-[#4F46E5]" aria-hidden="true" data-id="element-6215" data-mp="k6nlhz5hh0wk">↕</span>
              </span>
            </div>

            <div className={`${card} p-5`} data-id="element-6216" data-mp="f8cb9nba0odt">
              <OptionHeader icon={BoxIcon} title={t('Remplacer', 'Replace')} sub={t('Gérez le processus directement dans Requêtes ATNA.', 'Manage the process directly in ATNA Requests.')} data-id="element-6217" data-mp="1cut86rg3kzz1" />
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-6218" data-mp="12uagxbm7eh73">
                {REPLACE.map(({
                icon,
                title,
                desc
              }) => <li key={title.en} className="flex items-center gap-3 rounded-xl border border-[#EFEFF5] bg-[#FCFCFE] px-3 py-2.5" data-id="element-6219" data-mp="3nm2t6tqe2yej">
                    <IconDot icon={icon} size="sm" data-id="element-6220" data-mp="3jjo802z8zmk3" />
                    <span className="min-w-0" data-id="element-6221" data-mp="1z2ovchyl1id7">
                      <span className="block font-sans text-[12.5px] text-[#121212] leading-tight" data-id="element-6222" data-mp="1z3zycfk7em39">{pick(title)}</span>
                      <span className="block font-sans text-[11px] text-[#8A90A0] mt-0.5" data-id="element-6223" data-mp="234jupgwydloc">{pick(desc)}</span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="flex justify-center py-3" aria-hidden="true" data-id="element-6224" data-mp="2cvy3kfasnxu5">
            <ArrowDownIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-6225" data-mp="2hswt0kjijgm0" />
          </div>

          {/* Shared workflow */}
          <ol className={`${card} px-4 py-5 grid grid-cols-2 sm:grid-cols-4 lg:flex lg:items-start gap-y-6`} data-id="element-6226" data-mp="2mudscbmcmv0h">
            {STAGES.map(({
            icon,
            title,
            desc
          }, i) => <React.Fragment key={title.en} data-id="element-6227" data-mp="2vro6q5ugr09m" data-mpi={i}>
                <li className="flex-1 flex flex-col items-center text-center px-1" data-id="element-6228" data-mp="wfv51vwqd6qk">
                  <IconDot icon={icon} size="lg" data-id="element-6229" data-mp="wi7v6wcn6nep" />
                  <span className="font-sans text-[12.5px] font-semibold text-[#121212] mt-2.5" data-id="element-6230" data-mp="11fzmpp9dk7id">{pick(title)}</span>
                  <span className="font-sans text-[10.5px] text-[#8A90A0] mt-1 leading-snug max-w-[112px]" data-id="element-6231" data-mp="33b5p3fvz1k4l">{pick(desc)}</span>
                </li>
                {i < STAGES.length - 1 && <li className="hidden lg:flex items-center pt-4" aria-hidden="true" data-id="element-6232" data-mp="3d0yrbqa84mw6">
                    <ChevronRightIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={2} data-id="element-6233" data-mp="3848c1568tvbt" />
                  </li>}
              </React.Fragment>)}
          </ol>

          {/* Concrete journey */}
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_28px_minmax(0,1fr)_28px_minmax(0,1fr)_28px_minmax(0,1.05fr)] items-stretch" data-id="element-6234" data-mp="2gdu3loh01b7j">
            <div className={`${card} p-4`} data-id="element-6235" data-mp="2pc60274jr37w">
              <div className="flex items-start justify-between gap-2" data-id="element-6236" data-mp="2pbe7z6a5rnmd">
                <span className="flex items-center gap-2.5" data-id="element-6237" data-mp="9ki9quk1g8y2">
                  <IconDot icon={FileTextIcon} size="sm" data-id="element-6238" data-mp="4mqh1ybkkist" />
                  <span className="font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-6239" data-mp="4m8buc6xiu8r">{t('Demande citoyenne', 'Resident request')}</span>
                </span>
                <span className="text-right" data-id="element-6240" data-mp="1rjg853jnocq5">
                  <span className="block font-sans text-[12px] font-medium text-[#121212]" data-id="element-6241" data-mp="1rkhgv1d78n4p">#25473</span>
                  <span className="block font-sans text-[9.5px] text-[#8A90A0]" data-id="element-6242" data-mp="1dlhwi5wpyuec">{t('Aujourd’hui, 10 h 24', 'Today, 10:24 AM')}</span>
                </span>
              </div>
              <p className="mt-3 rounded-lg bg-[#F5F6F8] px-3 py-2.5 font-sans text-[12px] text-[#121212] leading-snug" data-id="element-6243" data-mp="269drn66n4e90">
                {t('« Un lampadaire ne fonctionne plus sur la rue des Érables. »', '“A streetlight is no longer working on rue des Érables.”')}
              </p>
              <p className="mt-2.5 flex items-center gap-1.5 font-sans text-[10.5px] text-[#5B6478]" data-id="element-6244" data-mp="21f168qllssum">
                <MapPinIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-6245" data-mp="2txoirhmjazk3" />
                Rue des Érables, Brossard
              </p>
            </div>

            <StepArrow data-id="element-6246" data-mp="3a98tfqf66orc" />

            <div className={`${card} p-4`} data-id="element-6247" data-mp="3f5zbukeg5wfx">
              <span className="flex items-center gap-2.5" data-id="element-6248" data-mp="3f4xpvkvw7pjf">
                <IconDot icon={SparklesIcon} size="sm" data-id="element-6249" data-mp="31fdpglpsb4da" />
                <span className="font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-6250" data-mp="31elvtgfqzgpn">ATNA</span>
              </span>
              <ul className="mt-3 space-y-1.5" data-id="element-6251" data-mp="35e31a9v6cdut">
                {[t('Éclairage public', 'Street lighting'), t('Priorité normale', 'Normal priority'), t('Doublon vérifié', 'Duplicate check done'), t('Adresse reconnue', 'Address recognized')].map(label => <li key={label} className="flex items-center gap-2 font-sans text-[11.5px] text-[#2A2F3C]" data-id="element-6252" data-mp="2mimqgdk0vuo7">
                    <CheckCircle2Icon className="w-3.5 h-3.5 text-[#22A55B] shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-6253" data-mp="gp4sorzfku6k" />
                    {label}
                  </li>)}
              </ul>
            </div>

            <StepArrow data-id="element-6254" data-mp="256686phkhxub" />

            <div className={`${card} p-4`} data-id="element-6255" data-mp="216xjhz1dwq5m">
              <span className="flex items-start gap-2.5" data-id="element-6256" data-mp="2188m3vj3frv5">
                <IconDot icon={WrenchIcon} size="sm" data-id="element-6257" data-mp="2eylsvuk910iw" />
                <span className="font-sans text-[12.5px] font-medium text-[#121212] leading-tight" data-id="element-6258" data-mp="2ezdeqdo1njvz">
                  Territoire
                  <span className="block" data-id="element-6259" data-mp="2ow8e0ys55obb">{t('(ou ATNA)', '(or ATNA)')}</span>
                </span>
              </span>
              <p className="font-sans text-[12.5px] text-[#121212] mt-3" data-id="element-6260" data-mp="2jxoqwv1ta6hd">{t('Intervention créée', 'Intervention created')}</p>
              <p className="mt-2 flex items-center gap-2 font-sans text-[11.5px] text-[#2A2F3C]" data-id="element-6261" data-mp="2xvn2jsoqzot6">
                <CalendarIcon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-6262" data-mp="2sz4y6txbxshd" />
                {t('SLA : 48 h', 'SLA: 48 h')}
              </p>
            </div>

            <StepArrow data-id="element-6263" data-mp="ykvn8jdhe475" />

            <div className={`${card} p-4`} data-id="element-6264" data-mp="yln6cupw69gj">
              <span className="flex items-center gap-2.5" data-id="element-6265" data-mp="2ymlfaqarnic2">
                <CheckCircle2Icon className="w-7 h-7 text-[#22A55B]" strokeWidth={1.6} aria-hidden="true" data-id="element-6266" data-mp="2yo6ami4wn5az" />
                <span className="font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-6267" data-mp="33mzpadvxh2cq">{t('Citoyen informé', 'Resident informed')}</span>
              </span>
              <p className="mt-3 flex gap-1.5 rounded-lg bg-[#ECF8F0] px-3 py-2.5 font-sans text-[12px] text-[#121212] leading-snug" data-id="element-6268" data-mp="3cmw4zq0caqvn">
                <CheckIcon className="w-3.5 h-3.5 text-[#22A55B] shrink-0 mt-0.5" strokeWidth={2.4} aria-hidden="true" data-id="element-6269" data-mp="3hitou6atafd6" />
                {t('Demande prise en charge.', 'Request being handled.')}
              </p>
              <p className="font-sans text-[10px] text-[#8A90A0] mt-2.5 text-center leading-snug" data-id="element-6270" data-mp="3rd3h7nzr7dnw">
                {t('Mise à jour envoyée automatiquement.', 'Update sent automatically.')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}